/**
 * Impersonation context – admin "Login as" another user across the whole app.
 * When active: read APIs pass actAsUserId; effective profile/uid used for display.
 * Persisted in sessionStorage so refresh keeps impersonation.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as impersonationStore from '../impersonationStore';
import { logUserActivity } from '../services/auditLogService';

const STORAGE_KEY = 'tt_impersonate';
const STORAGE_PROFILE_KEY = 'tt_impersonate_profile';

const ImpersonationContext = createContext();

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) return { isImpersonating: false, effectiveUserId: null, effectiveProfile: null, startImpersonating: () => {}, stopImpersonating: () => {} };
  return ctx;
}

export function ImpersonationProvider({ children }) {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const rawProfile = sessionStorage.getItem(STORAGE_PROFILE_KEY);
      if (raw && rawProfile) {
        const profile = JSON.parse(rawProfile);
        impersonationStore.setActAs(raw);
        return { userId: raw, profile };
      }
    } catch (_) {}
    return null;
  });

  // Clear impersonation when user logs out
  useEffect(() => {
    if (!currentUser?.uid && state) {
      setState(null);
      impersonationStore.clearActAs();
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!state) {
      impersonationStore.clearActAs();
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_PROFILE_KEY);
      } catch (_) {}
      return;
    }
    impersonationStore.setActAs(state.userId);
    try {
      sessionStorage.setItem(STORAGE_KEY, state.userId);
      sessionStorage.setItem(STORAGE_PROFILE_KEY, JSON.stringify(state.profile || {}));
    } catch (_) {}
  }, [state]);

  function startImpersonating(userId, profile, options = {}) {
    const p = profile || { displayName: 'Unknown', email: '' };
    impersonationStore.setActAs(userId); // Set immediately so refreshUserProfile sees it
    setState({ userId, profile: p });
    // Refresh AuthContext profile so getMyProfile (with actAsUserId) returns impersonated user's full profile
    if (typeof refreshUserProfile === 'function') refreshUserProfile();
    if (!options.skipAudit && currentUser?.uid) {
      logUserActivity(
        { userId: currentUser.uid, userEmail: currentUser.email, displayName: currentUser.displayName || currentUser.email },
        'login_as_start',
        '/admin',
        'Login as user',
        { targetUserId: userId, targetUserEmail: p?.email, targetUserName: p?.displayName }
      );
    }
  }

  function stopImpersonating(options = {}) {
    const prev = state;
    impersonationStore.clearActAs(); // Clear immediately
    setState(null);
    // Refresh AuthContext to restore real user's profile
    if (typeof refreshUserProfile === 'function') refreshUserProfile();
    if (!options.skipAudit && currentUser?.uid && prev?.userId) {
      logUserActivity(
        { userId: currentUser.uid, userEmail: currentUser.email, displayName: currentUser.displayName || currentUser.email },
        'login_as_stop',
        window.location.pathname || '/',
        'Stop Login as',
        { targetUserId: prev.userId }
      );
    }
  }

  const isImpersonating = !!state;
  const effectiveUserId = state?.userId ?? currentUser?.uid ?? null;
  const effectiveProfile = state?.profile ?? userProfile ?? null;

  const value = {
    isImpersonating,
    impersonatingUserId: state?.userId ?? null,
    impersonatedProfile: state?.profile ?? null,
    effectiveUserId,
    effectiveProfile,
    startImpersonating,
    stopImpersonating,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}
